import { FunctionComponent, Fragment, useState, useEffect } from 'react';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { useMediaQuery } from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPlusMinus } from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';
import _ from 'lodash';
import ProductVendor, { createEmptyVendor } from 'model/ProductVendor';
import { ProductVendorsStatus } from 'reducers/productVendors/ProductVendorsState';
import VendorDetails from './VendorDetails';
import Button from 'components/UI/V3/Button/Button';
import VendorProductLibrary from './VendorProductLibrary';
import { deleteThunk, fetchThunk as fetchVendorsThunk, saveThunk } from 'actions/productVendors/ProductVendorsActions';
import ConfirmDialog from '../UI/Dialog';
import { isEmailValid, validatePhoneNumber, validateZipcode } from '@spike/validations';
import { validateRequired } from 'utils/ValidationUtils';
import { FieldError } from '@spike/model';
import { fetchProductsThunk } from '@spike/products-action';
import { fetchThunk as fetchCategoriesThunk } from 'actions/productCategories/ProductCategoriesActions';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import WarningDialog from '../UI/WarningDialog';
import { useApiClientWrapper, useMarketplace } from 'hooks';

interface Props {
    onBack: (redirection: boolean) => void;
    vendor?: ProductVendor;
    redirection: boolean;
    vendorSaved: (vendor: ProductVendor) => void;
    onRedirection: () => void;
}

interface SectionState {
    collapsed: boolean;
    disabled: boolean;
    editable: boolean;
    completed: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            backgroundColor: '#FAFAFA',
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            minHeight: 0,
            overflow: 'hidden'
        },
        newVendorContainer: {
            [theme.breakpoints.down(768)]: {
                height: '100%'
            },
            [theme.breakpoints.between(768, 'md')]: {
                height: 'calc(100vh - 88px)'
            },
            [theme.breakpoints.up('lg')]: {
                height: 'calc(100vh - 110px)'
            }
        },
        headerContainer: {
            backgroundColor: '#FAFAFA',
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            flexShrink: 0,
            [theme.breakpoints.down(768)]: {
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '8px',
                flexWrap: 'wrap',
                gap: '8px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingLeft: '120px',
                paddingRight: '120px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingLeft: '150px',
                paddingRight: '150px'
            }
        },
        bodyContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            backgroundColor: '#FAFAFA',
            flex: 1,
            minHeight: 0,
            overflowX: 'hidden',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            [theme.breakpoints.down(768)]: {
                paddingLeft: '16px',
                paddingRight: '16px',
                paddingBottom: '24px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingLeft: '112px',
                paddingRight: '112px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingLeft: '140px',
                paddingRight: '140px'
            }
        },
        backContainer: {
            display: 'flex',
            position: 'absolute',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            width: '32px',
            height: '32px',
            [theme.breakpoints.down(768)]: {
                position: 'static',
                margin: 0,
                width: 'auto',
                height: 'auto',
                fontSize: '20px',
                flexShrink: 0
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '32px',
                marginLeft: '-72px',
                fontSize: '29px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '40px',
                marginLeft: '-90px',
                fontSize: '36px'
            }
        },
        titleButtonContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minWidth: 0,
            flex: 1,
            [theme.breakpoints.down(768)]: {
                width: 'auto',
                marginTop: 0,
                marginBottom: 0
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '25px',
                marginBottom: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '31px',
                marginBottom: '27px'
            }
        },
        titleContainer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            width: '100%',
            minWidth: 0
        },
        title: {
            fontWeight: 600,
            [theme.breakpoints.down(768)]: {
                fontSize: '20px',
                lineHeight: '26px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            },
            [theme.breakpoints.between(768, 'md')]: {
                fontSize: '29px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '36px'
            }
        },
        sectionContainer: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            [theme.breakpoints.down(768)]: {
                marginTop: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginTop: '31px'
            },
            [theme.breakpoints.up('lg')]: {
                marginTop: '39px'
            }
        },
        saveMessage: {
            width: '100%',
            textAlign: 'left',
            [theme.breakpoints.down('md')]: {
                fontSize: '12px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '15px'
            }
        },
        detailsAndLibraryContainer: {
            [theme.breakpoints.down(768)]: {
                marginBottom: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginBottom: '24px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '30px'
            }
        },
        saveButtonContainer: {
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'nowrap',
            gap: '8px',
            width: 'auto',
            flexShrink: 0,
            [theme.breakpoints.down(768)]: {
                width: 'auto',
                marginLeft: 'auto'
            }
        },
        saveButton: {
            width: 'auto',
            flexShrink: 0,
            [theme.breakpoints.down(768)]: {
                height: '40px',
                minWidth: 'unset',
                paddingLeft: '16px',
                paddingRight: '16px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                height: '39px',
                paddingLeft: '24px',
                paddingRight: '24px'
            },
            [theme.breakpoints.up('lg')]: {
                height: '49px',
                paddingLeft: '32px',
                paddingRight: '32px'
            }
        },
        changeStatusButton: {
            fontWeight: 600,
            float: 'right',
            cursor: 'pointer',
            color: '#92B4A7',
            [theme.breakpoints.down(768)]: {
                float: 'none',
                marginBottom: '12px',
                marginRight: 0,
                display: 'block',
                textAlign: 'right'
            },
            [theme.breakpoints.between(768, 'md')]: {
                marginBottom: '16px',
                marginRight: '29px'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '20px',
                marginRight: '36px'
            }
        },
        buttonContainer: {
            width: '100%'
        },
        textDialog: {
            fontWeight: 400,
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        addProductButton: {
            flexShrink: 0,
            width: 'auto',
            [theme.breakpoints.down(768)]: {
                height: '40px',
                width: '40px',
                minWidth: '40px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                height: '39px',
                paddingLeft: '16px',
                paddingRight: '16px'
            },
            [theme.breakpoints.up('lg')]: {
                height: '49px',
                paddingLeft: '20px',
                paddingRight: '20px'
            }
        },
        textdialog: {
            textAlign: 'center',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                lineHeight: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '27px'
            }
        },
        titleDialog: {
            fontWeight: 600,
            [theme.breakpoints.down('md')]: {
                fontSize: '19px',
                lineHeight: '29px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '24px',
                lineHeight: '36px'
            }
        },
        deleteContainer: {
            display: 'flex',
            justifyContent: 'flex-end'
        },
        deleteContent: {
            display: 'flex'
        },
        deleteBox: {
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            [theme.breakpoints.down(768)]: {
                paddingBottom: '24px'
            },
            [theme.breakpoints.between(768, 'md')]: {
                paddingBottom: '48px'
            },
            [theme.breakpoints.up('lg')]: {
                paddingBottom: '60px'
            }
        },
        iconDelete: {
            fontWeight: 500,
            color: '#7A7A7A',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                marginRight: '5px',
                marginTop: '2px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                marginRight: '6px',
                marginTop: '3px'
            }
        },
        delete: {
            fontWeight: 500,
            color: '#7A7A7A',
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        }
    })
);

enum ProductStep {
    NewStep,
    EditStep
}

export const VendorComponent: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:767.95px)');

    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();

    const [errors, setErrors] = useState<Array<FieldError>>([]);

    const newVendor = createEmptyVendor();
    const [vendor, setVendor] = useState(props.vendor ? props.vendor : newVendor);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [assignProducts, setAssignProducts] = useState(false);

    const [showWarningDeleteVendor, setShowWarningDeleteVendor] = useState(false);

    const [showWarningAssignedProducts, setShowWarningAssignedProducts] = useState(false);

    const [details, setDetails] = useState<SectionState>({
        collapsed: false,
        disabled: false,
        editable: true,
        completed: false
    });

    const [productLibrary, setProductLibrary] = useState<SectionState>({
        collapsed: false,
        disabled: false,
        editable: true,
        completed: false
    });

    const isNewVendor = () => {
        return props.vendor === undefined;
    };

    const loading = useSelector<RootState, boolean>(state => state.productVendors.loading);
    const status = useSelector<RootState, ProductVendorsStatus>(state => state.productVendors.status);

    const [step] = useState<ProductStep>(isNewVendor() ? ProductStep.NewStep : ProductStep.EditStep);

    useEffect(() => {
        if (status === ProductVendorsStatus.SaveSuccess) {
            props.vendorSaved(vendor);
            dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
            dispatch(fetchVendorsThunk());
            dispatch(fetchCategoriesThunk());
            props.onBack(false);
            setTimeout(() => {
                setUnsavedChanges(false);
            }, 1500);
            if (props.redirection) {
                props.onRedirection();
            }
        }
        if (status === ProductVendorsStatus.DeleteSuccess) {
            dispatch(fetchProductsThunk(apiClientWrapper, '', [], [apiClientWrapper.marketplaceId], [], 1, 10000));
            dispatch(fetchVendorsThunk());
            dispatch(fetchCategoriesThunk());
            props.onBack(false);
            setTimeout(() => {
                setUnsavedChanges(false);
            }, 1500);
        }
    }, [status]);

    useEffect(() => {
        setUnsavedChanges(!_.isEqual(props.vendor, vendor) && !_.isEqual(newVendor, vendor));
    }, [vendor]);

    useEffect(() => {
        if (props.vendor) {
            setVendor(props.vendor);
        }
    }, [props.vendor]);

    const detailsEditHandler = () => {
        setDetails(prev => {
            return { ...prev, collapsed: !prev.collapsed };
        });
    };

    const productLibraryEditHandler = () => {
        setProductLibrary(prev => {
            return { ...prev, collapsed: !prev.collapsed };
        });
    };

    const cleanErrors = () => {
        setErrors([]);
    };

    const validate = (vendorToValidate: ProductVendor) => {
        const errors: Array<FieldError> = [validateRequired('name', { ...vendorToValidate }, 'Company name')].filter(
            (t): t is FieldError => !!t
        );

        vendorToValidate.phone &&
            errors.push(
                ...validatePhoneNumber(vendorToValidate.phone, marketplace.basics.address.country!.id, 'phone')
            );

        if (vendorToValidate.email && !isEmailValid(vendorToValidate.email)) {
            errors.push({
                fieldName: 'email',
                errorMessage: 'Invalid Email format.'
            });
        }

        vendorToValidate.address &&
            vendorToValidate.address?.zipcode !== undefined &&
            errors.push(
                ...validateZipcode(vendorToValidate.address.zipcode, marketplace.basics.address.country?.id, 'zipcode')
            );

        setErrors([...errors]);

        return errors;
    };

    const saveHandler = () => {
        const validationErrors = validate(vendor);
        if (validationErrors.length === 0) {
            dispatch(saveThunk(vendor));
        }
    };

    const handleChangeVendor = (vendorModified: ProductVendor) => {
        cleanErrors();
        validate(vendorModified);
        setVendor(vendorModified);
    };

    const handleDeleteVendor = () => {
        if (vendor.products.length) {
            setShowWarningAssignedProducts(true);
        } else {
            if (vendor.id) {
                setShowWarningDeleteVendor(true);
            }
        }
    };

    const handleConfirmDeleteVendor = () => {
        const validationErrors = validate(vendor);
        if (validationErrors.length === 0) {
            dispatch(deleteThunk(vendor));
        }
    };

    const addVendorHeaderTitle = <Typography className={classes.title}>Add Vendor</Typography>;

    const newVendorNameHeaderTitle = (
        <Fragment>
            <Typography className={classes.title}>{vendor.name}</Typography>
        </Fragment>
    );
    const editVendorNameHeaderTitle = <Typography className={classes.title}>{vendor.name}</Typography>;

    const vendorNameHeaderTitle = isNewVendor() ? newVendorNameHeaderTitle : editVendorNameHeaderTitle;

    const headerTitle = vendor.name ? vendorNameHeaderTitle : addVendorHeaderTitle;

    const header = (
        <Fragment>
            <Box
                className={classes.backContainer}
                onClick={() => props.onBack(props.redirection)}
            >
                <FontAwesomeIcon icon={faArrowLeft} />
            </Box>
            <Box className={classes.titleButtonContainer}>
                <Box className={classes.titleContainer}>{headerTitle}</Box>
            </Box>
            <Box className={classes.saveButtonContainer}>
                {vendor.products.length > 0 && (
                    <Button
                        label="Add/Remove Products"
                        variant="outlined"
                        leftIcon={isMobile ? faPlusMinus : undefined}
                        onlyIcon={isMobile}
                        onClick={() => setAssignProducts(true)}
                        className={classes.addProductButton}
                    />
                )}
                <Button
                    label="Save"
                    onClick={saveHandler}
                    disabled={false}
                    isLoading={loading}
                    className={classes.saveButton}
                />
            </Box>
        </Fragment>
    );

    const newContent = (
        <Fragment>
            <Box className={classes.sectionContainer}>
                <Box className={classes.detailsAndLibraryContainer}>
                    <VendorDetails
                        {...details}
                        vendor={vendor}
                        onEdit={() => {}}
                        onChange={handleChangeVendor}
                        errors={errors}
                    />
                </Box>
                {vendor.id && (
                    <Box className={classes.deleteBox}>
                        <Box className={classes.deleteContainer}>
                            <Box
                                className={classes.deleteContent}
                                onClick={handleDeleteVendor}
                            >
                                <FontAwesomeIcon
                                    icon={faTrashAlt}
                                    className={classes.iconDelete}
                                />
                                <Typography className={classes.delete}>Delete Vendor</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Fragment>
    );

    const editContent = (
        <Fragment>
            <Box className={classes.sectionContainer}>
                <Box className={classes.detailsAndLibraryContainer}>
                    <VendorDetails
                        {...details}
                        vendor={vendor}
                        onEdit={detailsEditHandler}
                        onChange={handleChangeVendor}
                        errors={errors}
                    />
                </Box>
                <Box className={classes.detailsAndLibraryContainer}>
                    <VendorProductLibrary
                        {...productLibrary}
                        vendor={vendor}
                        onEdit={productLibraryEditHandler}
                        assignProducts={assignProducts}
                        onCloseAssignProducts={() => setAssignProducts(false)}
                    />
                </Box>
                {vendor.id && (
                    <Box className={classes.deleteBox}>
                        <Box className={classes.deleteContainer}>
                            <Box
                                className={classes.deleteContent}
                                onClick={handleDeleteVendor}
                            >
                                <FontAwesomeIcon
                                    icon={faTrashAlt}
                                    className={classes.iconDelete}
                                />
                                <Typography className={classes.delete}>Delete Vendor</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Fragment>
    );

    const stepContent = (): JSX.Element => {
        switch (step) {
            case ProductStep.NewStep: {
                return newContent;
            }
            default: {
                return editContent;
            }
        }
    };

    return (
        <Fragment>
            <Box className={clsx(classes.container, classes.newVendorContainer)}>
                <Box className={clsx(classes.headerContainer)}>{header}</Box>
                <Box className={clsx(classes.bodyContainer)}>
                    {stepContent()}
                    <ConfirmDialog
                        open={showWarningDeleteVendor}
                        centered
                        onCancel={() => setShowWarningDeleteVendor(false)}
                        onConfirm={handleConfirmDeleteVendor}
                        question={
                            <Typography className={classes.titleDialog}>
                                Are you sure you want to delete this vendor?
                            </Typography>
                        }
                        subQuestion={
                            <Typography className={classes.textdialog}>
                                Deleting vendor will permanently remove it from your database. You can always recreate
                                it in the future.
                            </Typography>
                        }
                        confirmButtonLabel="Delete"
                        cancelButtonLabel="Nevermind"
                    />
                    <WarningDialog
                        open={showWarningAssignedProducts}
                        title={`(${vendor.products.length}) products currently assigned to this vendor."`}
                        description="In order to delete, you must first remove products assigned to this vendor."
                        onOk={() => setShowWarningAssignedProducts(false)}
                    />
                </Box>
            </Box>
        </Fragment>
    );
};

export default VendorComponent;
